import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import ExternalFileUpload from 'external-files/ExternalFileUpload';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import * as NoteLinkedFileUtil from 'notebook/notes/attachments/NoteLinkedFileUtil';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {NoteLinkedGroupItemProps} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import NoteConstants, {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {isNoteLinkedGroup} from 'notebook/TypePredicates';
import {
  FromServerNoteLinkedFile,
  NoteLinkedFile,
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY} from 'ui-library/Colors';
import DnDCanvas from 'ui-library/drag-and-drop/DnDCanvas';
import {DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {DND_BLOCK_SPACING} from 'ui-library/drag-and-drop/DnDStyleConstants';
import {HoverPosition} from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import moveSourceAdjacentToTarget from 'ui-library/drag-and-drop/move-item-handlers/moveSourceAdjacentToTarget';
import {assertNever, ElementClipboardEvent} from 'util/Types';

import DraggableItem from './DraggableItem';
import {GridMovementDirection} from './KeyboardNavigationConstants';
import {
  GridPlaceholderNotePart,
  isNotePartGrid,
  NotePartGrid as NotePartGridType,
} from './NotePartGridGroupConstants';
import {
  computeRowsForFlowLayout,
  DraggableFlowItem,
  MAX_ITEM_WIDTH,
  MIN_ITEM_WIDTH,
} from './util/computeRowsForFlowLayout';
import {makeGridPlaceholderNotePart} from './util/gridPlaceholderUtils';
import {
  DEFAULT_GRID_PLACEHOLDER_WIDTH,
  GRID_IMAGE_ACCEPT,
  isGridInsertableImageType,
  MAX_GRID_IMAGES,
} from './util/imageTypeUtils';

const DEFAULT_CONTAINER_WIDTH = 700;
const {NOTE_LINKED_OBJECT} = NoteConstants;

function hasExternalFiles(dataTransferTypes: DataTransfer['types']): boolean {
  return Array.from(dataTransferTypes).includes('Files');
}

/**
 * Shared filtering and capping logic for files entering the grid (both
 * file-input and drag-and-drop paths). Returns the accepted subset and
 * shows user-facing warnings for rejected or overflow files.
 */
function filterAndCapGridFiles(allFiles: Array<File>, currentImageCount: number): Array<File> {
  const accepted = allFiles.filter((f) => isGridInsertableImageType(f.type));
  const rejected = allFiles.filter((f) => !isGridInsertableImageType(f.type));

  if (accepted.length === 0) {
    if (rejected.length > 0) {
      notifications.warn('Only supported image types can be added to the image grid.');
    }
    return [];
  }

  // TODO(BNCH-147263): Add webp to the allowlist once preview generation support is implemented.
  // TODO(BNCH-147272): Create a Helpdesk article for supported image types and link to it here.
  if (rejected.length > 0) {
    if (rejected.length <= 2) {
      const names = rejected.map((f) => f.name).join(' and ');
      notifications.warn(`${names} could not be added — this image type is not supported in the image grid.`);
    } else {
      notifications.warn(
        'Some image types (e.g. WebP, HEIC) are not supported in the image grid and were not added.'
      );
    }
  }

  const availableSlots = MAX_GRID_IMAGES - currentImageCount;
  if (availableSlots <= 0) {
    notifications.warn(`Maximum of ${MAX_GRID_IMAGES} images allowed side-by-side.`);
    return [];
  }

  if (accepted.length > availableSlots) {
    notifications.warn(
      `You can only add ${availableSlots} more image${availableSlots !== 1 ? 's' : ''}. Maximum of ${MAX_GRID_IMAGES} images allowed.`
    );
    return accepted.slice(0, availableSlots);
  }

  return accepted;
}

export interface NotePartGridProps {
  notePartCid: string;
  group: NotePartGridType;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  itemProps?: NoteLinkedGroupItemProps;
}

enum RelativePositionToNotePartGrid {
  Before, // Represents the notepart before the NotePartGrid
  Itself, // Represents the NotePartGrid itself
  After, // Represents the notepart after the NotePartGrid
}

/**
 * NotePartGrid renders a drag-and-drop grid of DraggableItems within a DnDCanvas.
 * Each item in the grid corresponds to a containedNotePart (e.g. an image attachment)
 * within a NoteLinkedGroup of kind NOTE_PART_GRID.
 */
export default function NotePartGrid({
  notePartCid,
  group,
  editor,
  editorProps,
  itemProps,
}: NotePartGridProps): JSX.Element {
  const selectNotePartInEntryRelativeToNotePartGrid = useCallback(
    (relativePosition: RelativePositionToNotePartGrid): boolean => {
      const notePartGridIndex = editor.getIndex(notePartCid);
      if (notePartGridIndex !== -1) {
        let desiredNotePartIndex: number;
        switch (relativePosition) {
          case RelativePositionToNotePartGrid.Itself:
            desiredNotePartIndex = notePartGridIndex;
            break;
          case RelativePositionToNotePartGrid.Before:
            // If NotePartGrid is at top of entry, we can't go "before", so select NotePartGrid itself
            desiredNotePartIndex = notePartGridIndex === 0 ? notePartGridIndex : notePartGridIndex - 1;
            break;
          case RelativePositionToNotePartGrid.After:
            // If NotePartGrid is at bottom of entry, can't go "after", so select NotePartGrid itself
            desiredNotePartIndex =
              notePartGridIndex === editor.props.note.noteParts.length - 1
                ? notePartGridIndex
                : notePartGridIndex + 1;
            break;
          default:
            throw new Error(`Unhandled relative position: ${assertNever(relativePosition)}`);
        }
        const desiredNotePart = editor.props.note.noteParts[desiredNotePartIndex];
        const selection = editor.selectionAtItem(desiredNotePart);
        if (selection != null) {
          editorProps.requestSelection(selection);
          return true;
        } else {
          return false;
        }
      }
      return false;
    },
    [editor, editorProps, notePartCid]
  );
  const [selectedIndexInGrid, setSelectedIndexInGrid] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const prevContainedNotePartsRef = useRef(group.containedNoteParts);
  const dragElementsRef = useRef(new Set<EventTarget>());

  // Use editor width for container width
  const containerWidth = (editor.state as {editorWidth?: number}).editorWidth || DEFAULT_CONTAINER_WIDTH;

  const isEmpty = group.containedNoteParts.length === 0;

  // Reset selectedIndexInGrid if it falls out of bounds (e.g. after undo/redo or failed upload removal)
  useEffect(() => {
    if (selectedIndexInGrid != null && selectedIndexInGrid >= group.containedNoteParts.length) {
      setSelectedIndexInGrid(
        group.containedNoteParts.length > 0 ? group.containedNoteParts.length - 1 : null
      );
    }
  }, [group.containedNoteParts.length, selectedIndexInGrid]);

  // If we delete an item in the notepartgrid, select the entire notepartgrid and unselect all photos within the grid.
  // If we add an item, select the added item if it's not a placeholder.
  useEffect(() => {
    const prev = prevContainedNotePartsRef.current;
    prevContainedNotePartsRef.current = group.containedNoteParts;

    if (prev.length < group.containedNoteParts.length) {
      // Item added — select it if it's a real item (not an upload placeholder)
      const prevCids = new Set(prev.map((p) => p.cid));
      const addedIndex = group.containedNoteParts.findIndex((p) => !prevCids.has(p.cid) && !p.placeholder);
      if (addedIndex !== -1) {
        setSelectedIndexInGrid(addedIndex);
      }
    } else if (prev.length > group.containedNoteParts.length) {
      // Item deleted. We need to distinguish between user-initiated deletions and
      // internal placeholder removals (e.g. after an upload failure).
      const currentCids = new Set(group.containedNoteParts.map((p) => p.cid));
      const removedItems = prev.filter((p) => !currentCids.has(p.cid));
      const removedOnlyPlaceholders = removedItems.length > 0 && removedItems.every((p) => p.placeholder);
      if (!removedOnlyPlaceholders) {
        // Real item deleted. Select the entire notepartgrid and unselect any specific photo inside the notepartgrid.
        const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
          RelativePositionToNotePartGrid.Itself
        );
        if (wasSelectionSuccessful) {
          setSelectedIndexInGrid(null);
        }
      }
    }
  }, [
    group.containedNoteParts,
    editor,
    editorProps,
    notePartCid,
    selectNotePartInEntryRelativeToNotePartGrid,
  ]);

  // Clear internal selection when clicking outside the grid container.
  // GridItemWrapper.handleMouseDown stopPropagation() prevents the mediocre editor from knowing about
  // clicks within the grid, so we mirror the editor's own pattern
  // (MultiSelectListUtil.multiWindowMouseDownHandler) with a document-level listener
  // that deselects when the user clicks anywhere outside.
  useEffect(() => {
    if (selectedIndexInGrid == null) {
      return;
    }

    const handleDocumentMouseDown = (e: MouseEvent): void => {
      if (gridContainerRef.current && !gridContainerRef.current.contains(e.target as Node)) {
        // Don't clear when clicking into portaled menus/dropdowns — matches the mediocre
        // editor's exclusion list so that interactions with context menus, image editor
        // popovers, etc. don't inadvertently deselect the grid item.
        const target = e.target;
        if (!(target instanceof Element)) {
          return;
        }
        if (target.closest('.dropdown-menu, .backdrop, .menu, .js-mediocre-active-edit, .attachDropdown')) {
          return;
        }
        setSelectedIndexInGrid(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [selectedIndexInGrid]);

  // Compute the 2D row layout from the flat containedNoteParts list.
  // This produces the Array<Array<DraggableFlowItem>> that DnDCanvas expects.
  // Only compute when we have items (computeRowsForFlowLayout asserts non-empty).
  //
  // Note: We need to account for the grid container having a 12px padding on each side and the 12px gap
  // between items in each row.
  const rows = useMemo(
    () =>
      isEmpty
        ? [[]]
        : computeRowsForFlowLayout(
            group.containedNoteParts,
            containerWidth - DND_BLOCK_SPACING * 2,
            MIN_ITEM_WIDTH,
            MAX_ITEM_WIDTH,
            0,
            12
          ),
    [group.containedNoteParts, containerWidth, isEmpty]
  );

  // itemIndexToRowIndex maps the 1-dimensional item index of each item in the NPG to the index of the
  // row that the item is in.
  //
  // rowIndexToFirstItemIndexInRow maps the index of each row to the 1-dimensional item index of the
  // first item that is in that row.
  const [itemIndexToRowIndex, rowIndexToFirstItemIndexInRow] = useMemo(() => {
    const itemIndexToRowIndex: Record<number, number> = {};
    const rowIndexToFirstItemIndexInRow: Record<number, number> = {};
    let currentItemIndex = 0;
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      rowIndexToFirstItemIndexInRow[rowIndex] = currentItemIndex;
      for (let _ = 0; _ < rows[rowIndex].length; _++) {
        itemIndexToRowIndex[currentItemIndex] = rowIndex;
        currentItemIndex++;
      }
    }
    return [itemIndexToRowIndex, rowIndexToFirstItemIndexInRow];
  }, [rows]);

  // Determine maxColumnCount from the computed layout (max items in any single row).
  const maxColumnCount = useMemo(() => Math.max(1, ...rows.map((row) => row.length)), [rows]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Persist an updated group back to the editor via itemUpdateHandler.
   */
  const updateGroup = useCallback(
    (updatedGroup: NotePartGridType): void => {
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex !== -1) {
        editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
      }
    },
    [editor, notePartCid]
  );

  // ── Upload ──────────────────────────────────────────────────────────────────

  const handleUploadClick = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Replace a placeholder note part with the actual uploaded NoteLinkedFile.
   */
  const replacePlaceholder = useCallback(
    (taskId: string, serverNoteLinkedFile: FromServerNoteLinkedFile): void => {
      // The note may have been locked while the upload was in flight. Silently discard; placeholder will be
      // cleaned up when the note reloads from the server after the lock resolves.
      if (editor.props.locked) {
        return;
      }

      // Generate a CID for the NoteLinkedFile and register it
      const noteLinkedFileCid = _.uniqueId('note-linked-object-');
      const cidIdMap: {[cid: string]: string | number} = {[noteLinkedFileCid]: serverNoteLinkedFile.id};
      editor.props.registerObjects(cidIdMap);

      const noteLinkedFileWithCid = {
        ...serverNoteLinkedFile,
        cid: noteLinkedFileCid,
      };

      // Create a proper note part for the uploaded file
      const notePartCidForFile = _.uniqueId('note-part-');
      const noteLinkedFilePart: NotePart = {
        ...makeDefaultItem(NOTE_LINKED_OBJECT),
        item_id: serverNoteLinkedFile.id,
        alignment: 'left',
        placeholder: false,
        cid: notePartCidForFile,
        item: noteLinkedFileWithCid as unknown as NotePart['item'],
      };

      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const newContainedNoteParts = currentGroup.containedNoteParts.map((part: NotePart) =>
        part.taskId === taskId
          ? {...noteLinkedFilePart, itemWidth: part.itemWidth ?? DEFAULT_GRID_PLACEHOLDER_WIDTH}
          : part
      );

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
    },
    [editor, notePartCid]
  );

  /**
   * Remove a placeholder note part on upload failure because an empty placeholder is not useful in the grid.
   */
  const removePlaceholder = useCallback(
    (taskId: string): void => {
      if (editor.props.locked) {
        return;
      }
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const newContainedNoteParts = currentGroup.containedNoteParts.filter(
        (part: NotePart) => part.taskId !== taskId
      );

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
    },
    [editor, notePartCid]
  );

  const wireUploadDeferred = useCallback(
    (taskId: string, deferred: JQueryPromise<FromServerNoteLinkedFile>, fileName?: string): void => {
      deferred
        .done((serverFile: FromServerNoteLinkedFile | undefined) => {
          if (serverFile != null) {
            replacePlaceholder(taskId, serverFile);
          } else {
            removePlaceholder(taskId);
            notifications.error(`Failed to upload image${fileName ? `: ${fileName}` : ''}`);
          }
        })
        .fail(() => {
          removePlaceholder(taskId);
          notifications.error(`Failed to upload image${fileName ? `: ${fileName}` : ''}`);
        });
    },
    [replacePlaceholder, removePlaceholder]
  );

  const uploadFiles = useCallback(
    (files: Array<File>): void => {
      const fileId = editor.props.fileId;
      const taskIdAndDeferreds = ExternalFileUpload.createTaskAndUploadFiles(fileId, files);

      const placeholderNoteParts = taskIdAndDeferreds.map(({taskId}: {taskId: string}) =>
        makeGridPlaceholderNotePart(taskId)
      );

      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: [...currentGroup.containedNoteParts, ...placeholderNoteParts],
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});

      // TODO(BNCH-145487): Replace _.defer with redux/store-based state management to avoid race
      // conditions when multiple files are uploaded in quick succession (same issue as NoteLinkedFile).
      _.defer(() => {
        taskIdAndDeferreds.forEach(
          (
            {
              taskId,
              fileUploadDeferred,
            }: {taskId: string; fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>},
            index: number
          ) => {
            wireUploadDeferred(taskId, fileUploadDeferred, files[index].name);
          }
        );
      });
    },
    [editor, notePartCid, wireUploadDeferred]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const allFiles = Array.from(e.target.files || []);
      const files = filterAndCapGridFiles(allFiles, group.containedNoteParts.length);
      e.target.value = '';
      if (files.length > 0) {
        uploadFiles(files);
      }
    },
    [group.containedNoteParts.length, uploadFiles]
  );

  // ── Wire up deferreds from placeholders created via NoteEditor drag-and-drop ──

  const processedCidsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const notePart of group.containedNoteParts) {
      if (!notePart.placeholder) {
        continue;
      }
      const placeholder = notePart as GridPlaceholderNotePart;
      if (!placeholder._fileUploadDeferred || processedCidsRef.current.has(placeholder.cid)) {
        continue;
      }
      processedCidsRef.current.add(placeholder.cid);

      const {taskId, _fileUploadDeferred: deferred, _fileName: fileName} = placeholder;
      _.defer(() => {
        wireUploadDeferred(taskId!, deferred, fileName);
      });
    }
  }, [group.containedNoteParts, wireUploadDeferred]);

  // ── DnDCanvas: onMoveItem ─────────────────────────────────────────────────

  const handleMoveItem = useCallback(
    (source: DraggableFlowItem, target: DraggableFlowItem, hoverPosition?: HoverPosition): void => {
      const {newLayout, layoutChanged} = moveSourceAdjacentToTarget(rows, source, target, hoverPosition);
      if (!layoutChanged) {
        return;
      }
      // Flatten the reordered 2D layout back into a 1D containedNoteParts array
      const reorderedNoteParts = newLayout.flat().map((item) => item.notePart);

      // Adjust selected index in grid if the currently selected item has shifted to a new position.
      if (selectedIndexInGrid != null) {
        const selectedCid = group.containedNoteParts[selectedIndexInGrid].cid;
        if (selectedCid) {
          const newIndex = reorderedNoteParts.findIndex((part) => part.cid === selectedCid);
          setSelectedIndexInGrid(newIndex >= 0 ? newIndex : null);
        }
      }

      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: reorderedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [rows, group, updateGroup, selectedIndexInGrid]
  );

  // ── DraggableItem callbacks ────────────────────────────────────────────────

  const handleSelectInGrid = useCallback((index: number): void => {
    setSelectedIndexInGrid(index >= 0 ? index : null);
  }, []);

  const handleMoveSelectionInGrid = useCallback(
    (index: number, direction: GridMovementDirection): void => {
      // Adjust selection if needed
      switch (direction) {
        case GridMovementDirection.Right: {
          if (index === group.containedNoteParts.length - 1) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.After
            );
            if (wasSelectionSuccessful) {
              setSelectedIndexInGrid(null);
            }
          } else {
            setSelectedIndexInGrid(index + 1);
          }
          break;
        }
        case GridMovementDirection.Left: {
          if (index === 0) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.Before
            );
            if (wasSelectionSuccessful) {
              setSelectedIndexInGrid(null);
            }
          } else {
            setSelectedIndexInGrid(index - 1);
          }
          break;
        }
        case GridMovementDirection.Up: {
          const currRowIndexInGrid = itemIndexToRowIndex[index];
          if (currRowIndexInGrid === 0) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.Before
            );
            if (wasSelectionSuccessful) {
              setSelectedIndexInGrid(null);
            }
          } else {
            setSelectedIndexInGrid(rowIndexToFirstItemIndexInRow[currRowIndexInGrid - 1]);
          }
          break;
        }
        case GridMovementDirection.Down: {
          const currRowIndexInGrid = itemIndexToRowIndex[index];
          if (currRowIndexInGrid === rows.length - 1) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.After
            );
            if (wasSelectionSuccessful) {
              setSelectedIndexInGrid(null);
            }
          } else {
            setSelectedIndexInGrid(rowIndexToFirstItemIndexInRow[currRowIndexInGrid + 1]);
          }
          break;
        }
        default:
          throw new Error(`Unhandled GridMovementDirection: ${assertNever(direction)}`);
      }
    },
    [
      itemIndexToRowIndex,
      rowIndexToFirstItemIndexInRow,
      selectNotePartInEntryRelativeToNotePartGrid,
      group.containedNoteParts.length,
      rows,
    ]
  );

  const handleRemoveItem = useCallback(
    (index: number): void => {
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts.splice(index, 1);

      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);

      // Adjust selection if needed
      if (selectedIndexInGrid != null) {
        if (index === selectedIndexInGrid) {
          setSelectedIndexInGrid(null);
        } else if (index < selectedIndexInGrid) {
          setSelectedIndexInGrid(selectedIndexInGrid - 1);
        }
      }
    },
    [group, updateGroup, selectedIndexInGrid]
  );

  const handleFinalizeItemWidth = useCallback(
    (index: number, width: number): void => {
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts[index] = {
        ...newContainedNoteParts[index],
        itemWidth: width,
      };
      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [group, updateGroup]
  );

  const handleUpdateNoteLinkedObject = useCallback(
    (index: number, updates: Partial<NoteLinkedFile>): void => {
      const notePart = group.containedNoteParts[index];
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts[index] = {
        ...notePart,
        item: {...(notePart.item as NoteLinkedFile), ...updates},
      };
      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [group, updateGroup]
  );

  // Use the same CID registration flow as top-level NoteLinkedFiles so that
  // the save payload includes the updated tfiFileMeta and the item_id stays current.
  const handleSetUpdatedNoteLinkedFile = useCallback(
    (index: number, noteLinkedFile: FromServerNoteLinkedFile): void => {
      const currentNotePart = group.containedNoteParts[index];
      const cidIdMap: CidIdMap = {};
      const updatedNotePart = NoteLinkedFileUtil.maybeReplaceExternalFile(
        currentNotePart,
        currentNotePart,
        noteLinkedFile,
        null,
        cidIdMap
      );
      editor.props.registerObjects(cidIdMap);

      const newServerId = Object.values(cidIdMap)[0] ?? updatedNotePart.item_id;
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts[index] = {...updatedNotePart, item_id: newServerId};
      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [editor, group, updateGroup]
  );

  const handleSetClipboard = useCallback(
    (e: ElementClipboardEvent): void => {
      if (selectedIndexInGrid == null) {
        return;
      }
      const notePartGridIndex = editor.getIndex(notePartCid);
      if (notePartGridIndex === -1) {
        return;
      }

      const notePartGrid = editor.props.note.noteParts[notePartGridIndex];
      // Copy the entire notepartgrid, but only include the selected item in containedNoteParts
      let itemForClipboard = {
        ...notePartGrid,
        item: {
          ...notePartGrid.item,
          containedNoteParts: [group.containedNoteParts[selectedIndexInGrid]],
        },
      };
      if (editor.itemHasText?.(itemForClipboard)) {
        itemForClipboard = editor.withUpdatedText(itemForClipboard, {
          text: '',
          formats: formatters.defaultFormats(),
        });
      }
      if (e.clipboardData) {
        e.clipboardData.setData('text/html', editor.processItemsForHTMLClipboard([itemForClipboard]));
        e.clipboardData.setData('text/plain', editor.processItemsForTextClipboard([itemForClipboard]));
      }
    },
    [selectedIndexInGrid, group.containedNoteParts, editor, notePartCid]
  );

  const handlePaste = useCallback(
    async (e: ElementClipboardEvent): Promise<void> => {
      if (!e.clipboardData) {
        return;
      }
      // If no item is selected inside the NotePartGrid, return
      if (selectedIndexInGrid == null) {
        return;
      }

      const {items}: {items?: Array<NotePart>} = await editor.parseItemsFromClipboard(e, {
        fileId: editorProps.fileId,
      });

      // If there are no items, return
      if (!items || items.length === 0) {
        return;
      }

      // TODO(BNCH-147462): Refactor this logic according to the comments on the PR
      // The individual items that we will splice into the NotePartGrid
      const itemsToPaste: Array<NotePart> = items.flatMap((containerItem: NotePart) => {
        // We only want to paste NoteLinkedFiles or NotePartGrids. Everything else should be rejected.
        // We combine all valid images across all NLFs and NPGs copied, and will splice them into the current NPG.
        if (
          editor.props.getType(containerItem) === ModelTypeConstants.NOTE_LINKED_FILE &&
          containerItem.item != null &&
          (containerItem.item as NoteLinkedFile).previewFile?.previewType === EXTERNAL_FILE_TYPE.IMAGE
        ) {
          return [{...containerItem, itemWidth: DEFAULT_GRID_PLACEHOLDER_WIDTH}];
        } else if (
          editor.props.getType(containerItem) === NoteLinkedObjectType.NOTE_LINKED_GROUP &&
          isNotePartGrid(containerItem.item as NoteLinkedObject)
        ) {
          const notePartGrid = containerItem.item as NotePartGridType;
          const nonPlaceholderContainedItems = notePartGrid.containedNoteParts.filter(
            (np: NotePart) => np.item != null
          );

          // If we are attempting to copy/paste a NotePartGrid with 0 non-placeholder items, skip
          if (nonPlaceholderContainedItems.length === 0) {
            return [];
          }

          return nonPlaceholderContainedItems.map((containedItem: NotePart) => ({
            ...containedItem,
            itemWidth: DEFAULT_GRID_PLACEHOLDER_WIDTH,
          }));
        } else {
          return [];
        }
      });

      if (itemsToPaste.length === 0) {
        return;
      }

      // Store state for undo support
      if (editorProps.storeState != null) {
        editorProps.storeState(null);
      }

      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }

      const currentNoteLinkedObject = editor.props.note.noteParts[notePartIndex].item;
      assert(
        currentNoteLinkedObject != null &&
          isNoteLinkedGroup(currentNoteLinkedObject) &&
          isNotePartGrid(currentNoteLinkedObject),
        'Notebook changed during paste'
      );
      const currentNotePartGrid: NotePartGridType = currentNoteLinkedObject;

      // Replace the selected item with itemsToPaste by splicing them in
      const updatedContainedNoteParts = currentNotePartGrid.containedNoteParts.toSpliced(
        selectedIndexInGrid,
        1,
        ...itemsToPaste
      );

      const updatedNoteLinkedGroup = {
        ...currentNotePartGrid,
        containedNoteParts: updatedContainedNoteParts,
      };
      updateGroup(updatedNoteLinkedGroup);
    },
    [editor, editorProps, notePartCid, updateGroup, selectedIndexInGrid]
  );

  // Clicking empty space in the grid container deselects the current item.
  // GridItemWrapper.handleMouseDown calls stopPropagation(), so any click that
  // reaches this handler is guaranteed to be outside an item's content area.
  // Note: We use mouseDown instead of click here because of the complex interactions between the grid,
  // editor, and DnDCanvas which can, for example, prevent clicks from "completing" on an individual item in
  // the grid, and instead "starting" on a drag handle, but "finishing" on the grid container.
  const handleContainerMouseDown = useCallback((): void => {
    setSelectedIndexInGrid(null);
  }, []);

  // ── External file drag-and-drop ─────────────────────────────────────────

  const handleDragEnter = useCallback((e: React.DragEvent): void => {
    if (hasExternalFiles(e.dataTransfer.types)) {
      dragElementsRef.current.add(e.target);
      setIsDragOver(true);
      e.stopPropagation();
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent): void => {
    if (!hasExternalFiles(e.dataTransfer.types)) {
      return;
    }
    dragElementsRef.current.delete(e.target);
    if (dragElementsRef.current.size === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleExternalDragOverCapture = useCallback((e: React.DragEvent): void => {
    if (hasExternalFiles(e.dataTransfer.types)) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleExternalDropCapture = useCallback(
    (e: React.DragEvent): void => {
      if (!hasExternalFiles(e.dataTransfer.types)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      dragElementsRef.current.clear();
      setIsDragOver(false);

      if (editorProps.locked) {
        return;
      }

      const files = filterAndCapGridFiles(Array.from(e.dataTransfer.files), group.containedNoteParts.length);
      if (files.length > 0) {
        uploadFiles(files);
      }
    },
    [editorProps.locked, group.containedNoteParts.length, uploadFiles]
  );

  // ── DnDCanvas: renderItem ─────────────────────────────────────────────────
  // DnDBlock memoizes the rendered element via useMemo keyed on the
  // renderItem reference. The callback identity changes when selection
  // state changes (selectedIndexInGrid) or when CID→ID resolution
  // updates after a save (itemProps?.resolvedContainedItemIds).

  const renderItem = useCallback(
    (item: DraggableFlowItem, dragHandleProps: DnDDragHandleProps, _index: number): JSX.Element => (
      <DraggableItem
        item={item}
        dragHandleProps={dragHandleProps}
        editor={editor}
        editorProps={editorProps}
        isSelectedInGrid={selectedIndexInGrid === item.index}
        onRemoveItem={handleRemoveItem}
        onFinalizeItemWidth={handleFinalizeItemWidth}
        onSelectInGrid={handleSelectInGrid}
        onMoveSelectionInGrid={handleMoveSelectionInGrid}
        onSetClipboard={handleSetClipboard}
        onPaste={handlePaste}
        requestSelection={editorProps.requestSelection}
        isLockedWithEditWarn={editor.isLockedWithEditWarn}
        onUpdateNoteLinkedObject={handleUpdateNoteLinkedObject}
        onSetUpdatedNoteLinkedFile={handleSetUpdatedNoteLinkedFile}
      />
    ),
    // resolvedContainedItemIds is not read in the callback but is included to bust DnDBlock's useMemo
    // when CID→ID mappings change after a save (editorProps reference can remain stable across saves).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editor,
      editorProps,
      selectedIndexInGrid,
      handleRemoveItem,
      handleFinalizeItemWidth,
      handleSelectInGrid,
      handleMoveSelectionInGrid,
      handleSetClipboard,
      handlePaste,
      handleUpdateNoteLinkedObject,
      handleSetUpdatedNoteLinkedFile,
      itemProps?.resolvedContainedItemIds,
    ]
  );

  // ── DnDCanvas: row containers ──────────────────────────────────────────────

  const renderDndRowContainer = useCallback(
    ({children}: {children: JSX.Element}, rowIndex: number): JSX.Element => (
      <div key={`dnd-row-${rowIndex}`} className={css(styles.dndRow)}>
        {children}
      </div>
    ),
    []
  );

  const itemContainers = useMemo(
    () =>
      rows.map(
        (_: unknown, i: number) => (props: {children: JSX.Element}) => renderDndRowContainer(props, i)
      ),
    [rows, renderDndRowContainer]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SelectableNotePart indentation={0}>
      <div
        ref={gridContainerRef}
        className={css(styles.gridContainer, isDragOver && styles.gridContainerDragOver)}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOverCapture={handleExternalDragOverCapture}
        onDropCapture={handleExternalDropCapture}
        {...getTestProps({component: 'NotePartGrid', element: 'gridContainer'})}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={true}
          accept={GRID_IMAGE_ACCEPT}
          aria-label="Upload images"
          onChange={handleFileChange}
          className={css(styles.hiddenFileInput)}
          disabled={editorProps.locked}
        />
        {isEmpty ? (
          <div className={css(styles.emptyState)}>
            <div className={css(styles.emptyStateContent)}>
              <div className={css(styles.emptyStateText)}>Add images to view them side-by-side</div>
              {!editorProps.locked && (
                <Button
                  onClick={handleUploadClick}
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  text="Upload Images"
                />
              )}
            </div>
          </div>
        ) : (
          <div onMouseDown={handleContainerMouseDown}>
            <DnDCanvas
              items={rows}
              maxColumnCount={maxColumnCount}
              minBlockWidth={MIN_ITEM_WIDTH}
              fitContent={true}
              onMoveItem={handleMoveItem}
              renderItem={renderItem}
              itemContainers={itemContainers}
            />
          </div>
        )}
      </div>
    </SelectableNotePart>
  );
}

const styles = StyleSheet.create({
  gridContainer: {
    position: 'relative',
    width: '100%',
  },
  gridContainerDragOver: {
    backgroundColor: `${BLUE_LIGHT}14`,
    borderRadius: '8px',
    outline: `2px dashed ${BLUE_LIGHT}`,
    outlineOffset: '-2px',
  },
  hiddenFileInput: {
    display: 'none',
  },
  emptyState: {
    alignItems: 'center',
    border: `2px dashed ${GRAY}`,
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '200px',
    padding: '32px',
  },
  emptyStateContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    textAlign: 'center' as const,
  },
  emptyStateText: {
    color: GRAY,
    fontSize: '14px',
  },
  dndRow: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
  },
});
